import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Send, MessageSquare, Clock } from "lucide-react";
import { toast } from "sonner";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success("Message sent! We'll get back to you soon.");
    setFormData({ name: "", email: "", subject: "", message: "" });
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-indigo-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <MessageSquare className="w-8 h-8" />
            <h1 className="font-heading font-black text-3xl">Contact & Support</h1>
          </div>
          <p className="text-white/80 mt-2">Need help or have a question? We respond fast.</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Info */}
          <div>
            <h2 className="font-heading font-bold text-2xl text-slate-800 mb-6">Get in Touch</h2>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Email Us</h3>
                  <p className="text-slate-600">support@clearpathtestprep.com</p>
                  <p className="text-sm text-slate-400 mt-1">We typically respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-lime-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-lime-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Support Hours</h3>
                  <p className="text-slate-600">Monday – Friday</p>
                  <p className="text-sm text-slate-400 mt-1">9:00 AM – 5:00 PM CT</p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-indigo-50 rounded-2xl border-2 border-indigo-100">
              <h3 className="font-bold text-slate-800 mb-2">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/faq" className="text-indigo-600 hover:underline">
                    → Check our FAQ first
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-indigo-600 hover:underline">
                    → Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-indigo-600 hover:underline">
                    → Terms of Use
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-6">Send us a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block font-bold text-slate-700 mb-2">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 bg-slate-50 focus:border-indigo-500 focus:outline-none"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 bg-slate-50 focus:border-indigo-500 focus:outline-none"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-2">What do you need help with?</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 bg-slate-50 focus:border-indigo-500 focus:outline-none"
                >
                  <option value="">Select a topic</option>
                  <option value="account">Account Issues</option>
                  <option value="billing">Billing & Subscription</option>
                  <option value="technical">Technical Problem</option>
                  <option value="feedback">Feedback & Suggestions</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 focus:border-indigo-500 focus:outline-none resize-none"
                  placeholder="How can we help you?"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-6 text-center text-slate-500 text-sm">
        © 2025 Clear Path Testing Solutions LLC. All rights reserved.
      </footer>
    </div>
  );
};

export default Contact;
